import type { Gender } from '@/features/members';
import { httpClient } from '@/services/http/http-client';
import type { Page } from '@/types/api.types';

import {
  type ConversionResult,
  conversionResultSchema,
  type Prospect,
  type ProspectInput,
  prospectSchema,
  type ProspectStatus,
} from '../schemas/prospect.schema';

/** The only place this feature talks to the network. */

export interface ProspectFilter {
  page: number;
  pageSize: number;
  q?: string | undefined;
  status?: ProspectStatus | undefined;
  /**
   * `due`: open enquiries with a follow-up today or earlier; `overdue`: before today ("today" is
   * the gym's, decided by the API). Sorted by follow-up date, earliest first.
   */
  followUp?: 'due' | 'overdue' | undefined;
}

export function listProspects(
  filter: ProspectFilter,
  signal?: AbortSignal,
): Promise<Page<Prospect>> {
  return httpClient.getPage('/prospects', { schema: prospectSchema, query: { ...filter }, signal });
}

export function createProspect(input: ProspectInput): Promise<Prospect> {
  return httpClient.post('/prospects', input, { schema: prospectSchema });
}

export function updateProspect(id: number, input: Partial<ProspectInput>): Promise<Prospect> {
  return httpClient.patch(`/prospects/${String(id)}`, input, { schema: prospectSchema });
}

export interface ConvertInput {
  firstName: string;
  lastName: string | null;
  mobile: string;
  email: string | null;
  /** Required by the API on a new member profile. */
  dateOfBirth: string;
  gender: Gender;
  emergencyContactName: string | null;
  emergencyContactMobile: string | null;
}

/** Creates the member and marks the enquiry converted, in one transaction on the server. */
export function convertProspect(id: number, input: ConvertInput): Promise<ConversionResult> {
  return httpClient.post(`/prospects/${String(id)}/convert`, input, {
    schema: conversionResultSchema,
  });
}
